/**
 * ytdColumn — which cell supplies a metric's year-to-date reading.
 *
 * The master already computes YTD_Value on every row, as a running window down the year
 * (SUM for values and counts, AVG for percentages and days). So the column is not a
 * calculation, it is a CHOICE OF ROW — and the naive choices are both wrong:
 *
 *   the last month in the window   Revenue's December row is a forecast. A year-to-date
 *                                  that includes four months which have not happened is
 *                                  not a year-to-date.
 *   the last month with a value    Revenue Firm has values from August to December and
 *                                  none before. Its "YTD" would be £3.8m of order book
 *                                  that has been secured, not delivered. Their own report
 *                                  leaves that cell blank, and blank is right.
 *
 * So: THE LAST CLOSED PERIOD THAT CARRIES A YTD. Revenue lands on July (£2,763k), Past Due
 * lands on JUNE (£781k) because its actuals stop there, and Revenue Firm lands nowhere and
 * reads the honest em-dash. All three match the client's report.
 *
 * Actual and Target are resolved INDEPENDENTLY, because they do not always land on the same
 * month: Past Due has a July plan and no July actual. That is also why this module returns
 * the period each leg is as at — a reader comparing £781k against £525k deserves to know
 * the two cover different windows, and nothing else on the page would tell them.
 */

import type { Cell, PlanType } from '../../types/metrics'
import { isPeriodComplete } from './cellFormat'

export interface YtdLeg {
  cell: Cell
  /** The month this leg's year-to-date runs to — not always the last month of the window. */
  period: string
}

export interface YtdReading {
  actual: YtdLeg | null
  target: YtdLeg | null
  /** True when both legs run to the same month, so the pair is a like-for-like comparison. */
  comparable: boolean
}

type Lookup = (metricId: string, period: string, plan: PlanType) => Cell | null

function lastClosedWithYtd(
  metricId: string,
  periods: string[],
  plan: PlanType,
  cellFor: Lookup
): YtdLeg | null {
  for (let i = periods.length - 1; i >= 0; i -= 1) {
    const period = periods[i]
    if (!isPeriodComplete(period)) continue
    const cell = cellFor(metricId, period, plan)
    if (cell && cell.ytd_value !== null) return { cell, period }
  }
  return null
}

export function ytdFor(metricId: string, periods: string[], cellFor: Lookup): YtdReading {
  const actual = lastClosedWithYtd(metricId, periods, 'Actual', cellFor)
  const target = lastClosedWithYtd(metricId, periods, 'Target', cellFor)
  return {
    actual,
    target,
    comparable: actual !== null && target !== null && actual.period === target.period,
  }
}
